import { useBakerUserPermissions } from '@bakerweb/auth';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_DASHBOARD = '/dashboard';

export const BAKER_PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  general: {
    inventory: path(ROOTS_DASHBOARD, '/inventory'),
    cashflows: path(ROOTS_DASHBOARD, '/cash-flows'),
    bondSwap: path(ROOTS_DASHBOARD, '/bond-swap'),
    houstonTrust: path(ROOTS_DASHBOARD, '/houston-trust'),
    empyreanImport: path(ROOTS_DASHBOARD, '/empyrean-import'),
    yieldBookMonitor: path(ROOTS_DASHBOARD, '/yield-book-monitor'),
    searchBonds: path(ROOTS_DASHBOARD, '/search'),
    client: path(ROOTS_DASHBOARD, '/client/:portfolioId'),
    admin: path(ROOTS_DASHBOARD, '/admin'),
    risk: path(ROOTS_DASHBOARD, '/admin/risk'),
    upload: path(ROOTS_DASHBOARD, '/admin/upload'),
    apm: path(ROOTS_DASHBOARD, '/apm'),
    irrm: path(ROOTS_DASHBOARD, '/reports'),
    cusip: path(ROOTS_DASHBOARD, '/client/:portfolioId/cusip/:positionId'),
    reports: path(ROOTS_DASHBOARD, '/reports')
  },
  search: {
    general: path(ROOTS_DASHBOARD, '/search/general'),
    muni: path(ROOTS_DASHBOARD, '/search/muni')
  },
  lsegPricing: {
    root: path(ROOTS_DASHBOARD, '/lseg'),
    onDemand: path(ROOTS_DASHBOARD, '/lseg/on-demand'),
    dailyPricingBonds: path(ROOTS_DASHBOARD, '/lseg/daily-pricing-bonds')
  },
  loans: {
    root: path(ROOTS_DASHBOARD, '/loans'),
    autos: path(ROOTS_DASHBOARD, '/loans/autos'),
    consumers: path(ROOTS_DASHBOARD, '/loans/consumers'),
    cre: path(ROOTS_DASHBOARD, '/loans/cre'),
    general: path(ROOTS_DASHBOARD, '/loans/general'),
    residential: path(ROOTS_DASHBOARD, '/loans/residential')
  },
  admin: {
    root: path(ROOTS_DASHBOARD, '/admin'),
    yieldBookJsonPayloads: path(ROOTS_DASHBOARD, '/admin/yield-book/json-payloads'),
    yieldBookJsonPayloadDetail: (recordId: string) => path(ROOTS_DASHBOARD, `/admin/yield-book/json-payloads/${recordId}`),
    baker: {
      users: path(ROOTS_DASHBOARD, '/admin/baker/users'),
      editUser: (userId: string) => path(ROOTS_DASHBOARD, `/admin/baker/users/${userId}`)
    },
    clientPortal: {
      users: path(ROOTS_DASHBOARD, '/admin/client-portal/users'),
      portfolios: path(ROOTS_DASHBOARD, '/admin/client-portal/portfolios'),
      portfolioDetails: (portfolioCode: string) =>
        path(ROOTS_DASHBOARD, `/admin/client-portal/portfolios/${portfolioCode}`),
      newUser: path(ROOTS_DASHBOARD, '/admin/client-portal/users/new'),
      editUser: (userId: string) => path(ROOTS_DASHBOARD, `/admin/client-portal/users/${userId}`)
    },
    trusts: {
      users: path(ROOTS_DASHBOARD, '/admin/trusts/users'),
      newUser: path(ROOTS_DASHBOARD, '/admin/trusts/users/new'),
      editUser: (userId: string) => path(ROOTS_DASHBOARD, `/admin/trusts/users/${userId}`)
    }
  }
};

export type BakerNavChild = {
  title: string;
  path: string;
};

export type BakerNavItem = {
  title: string;
  path: string;
  icon?: React.ReactElement;
  children?: BakerNavChild[];
};

export type BakerNavGroup = {
  subheader: string;
  /**
   * Marks a work-in-progress section. Beta groups are gated to admins in the
   * builder below and rendered with a "Beta" badge by the nav/dashboard.
   */
  beta?: boolean;
  items: BakerNavItem[];
};

/** A single navigable destination, flattened from the group/item/children tree. */
export type BakerNavLink = {
  title: string;
  path: string;
  icon?: React.ReactElement;
  /**
   * The parent item's title, when it adds information the group header doesn't
   * already convey (e.g. "Search Bonds" for its General/Muni children). Undefined
   * when the parent item just mirrors the group (e.g. the lone "Admin" item in the
   * "Admin" group), so the menu/dashboard don't render a redundant label.
   */
  parentLabel?: string;
};

/**
 * Flattens a group into its leaf destinations. An item with children expands to
 * one link per child (carrying the parent's title as `parentLabel` unless it just
 * echoes the group header); a leaf item becomes a single link. Used by both the
 * menu-bar dropdowns and the dashboard cards so they stay in lockstep.
 */
export function groupLinks(group: BakerNavGroup): BakerNavLink[] {
  return group.items.flatMap((item) => {
    if (item.children && item.children.length > 0) {
      // Only label the parent when the group has several items AND the parent
      // name adds something beyond the group header — otherwise the children
      // read fine flat under the group's own heading.
      const parentLabel = group.items.length > 1 && item.title !== group.subheader ? item.title : undefined;
      return item.children.map((child) => ({
        title: child.title,
        path: child.path,
        icon: item.icon,
        parentLabel
      }));
    }
    return [{ title: item.title, path: item.path, icon: item.icon }];
  });
}

/**
 * The single source of truth for Baker's top-level navigation, consumed by both
 * the menu-bar nav (`BakerTopNav`) and the dashboard landing cards
 * (`DashboardHome`). Returns only the groups the signed-in user may see.
 */
export const useBakerNavConfig = (): BakerNavGroup[] => {
  const {
    isAdmin,
    isInventory,
    isLoans,
    isLseg,
    isDailyPricing,
    isReports,
    isSwapIQ,
    isHoustonTrust,
    isEmpyreanImport,
    isYieldBookMonitor
  } = useBakerUserPermissions();

  const config: BakerNavGroup[] = [];

  if (isLseg || isDailyPricing || isAdmin) {
    const children: BakerNavChild[] = [];
    if (isLseg || isAdmin) {
      children.push({
        title: 'LSEG Pricing',
        path: BAKER_PATH_DASHBOARD.lsegPricing.onDemand
      });
    }
    if (isDailyPricing || isAdmin) {
      children.push({
        title: 'Daily Pricing Bonds',
        path: BAKER_PATH_DASHBOARD.lsegPricing.dailyPricingBonds
      });
    }
    config.push({
      subheader: 'LSEG Pricing',
      items: [
        {
          title: 'LSEG Pricing',
          path: BAKER_PATH_DASHBOARD.lsegPricing.root,
          icon: <MonetizationOnIcon />,
          children
        }
      ]
    });
  }

  if (isInventory || isAdmin) {
    config.push({
      subheader: 'Inventory',
      items: [{ title: 'Items', path: BAKER_PATH_DASHBOARD.general.inventory, icon: <InventoryIcon /> }]
    });
  }

  if (isSwapIQ || isHoustonTrust || isAdmin) {
    const items: BakerNavItem[] = [];
    if (isSwapIQ || isAdmin) {
      items.push({
        title: 'Swap IQ',
        path: BAKER_PATH_DASHBOARD.general.bondSwap,
        icon: <SwapHorizontalCircleIcon />
      });
    }
    if (isHoustonTrust || isAdmin) {
      items.push({
        title: 'Houston Trust',
        path: BAKER_PATH_DASHBOARD.general.houstonTrust,
        icon: <SwapHorizontalCircleIcon />
      });
    }
    config.push({
      subheader: 'Clients',
      items
    });
  }

  if (isLoans || isAdmin) {
    config.push({
      subheader: 'Loans',
      items: [
        {
          title: 'Loans (Beta)',
          path: BAKER_PATH_DASHBOARD.loans.root,
          icon: <CreditScoreIcon />,
          children: [
            { title: 'Autos', path: BAKER_PATH_DASHBOARD.loans.autos },
            { title: 'Consumers', path: BAKER_PATH_DASHBOARD.loans.consumers },
            { title: 'CRE', path: BAKER_PATH_DASHBOARD.loans.cre },
            { title: 'General', path: BAKER_PATH_DASHBOARD.loans.general },
            { title: 'Residential', path: BAKER_PATH_DASHBOARD.loans.residential }
          ]
        }
      ]
    });
  }

  if (isReports || isAdmin) {
    config.push({
      subheader: 'Reports',
      items: [
        {
          title: 'Reports',
          path: BAKER_PATH_DASHBOARD.general.reports,
          icon: <FolderOpenIcon />
        }
      ]
    });
  }

  if (isEmpyreanImport || isAdmin) {
    config.push({
      subheader: 'Empyrean',
      items: [
        {
          title: 'Empyrean Import',
          path: BAKER_PATH_DASHBOARD.general.empyreanImport,
          icon: <UploadFileIcon />
        }
      ]
    });
  }

  // Gated by the `yieldBookMonitor` feature permission (or admin), so it can be
  // granted to specific users via the Baker Users admin page — not admin-only.
  if (isYieldBookMonitor || isAdmin) {
    config.push({
      subheader: 'Monitoring',
      items: [
        {
          title: 'Yield Book Monitor',
          path: BAKER_PATH_DASHBOARD.general.yieldBookMonitor,
          icon: <MonitorHeartIcon />
        }
      ]
    });
  }

  if (isAdmin) {
    config.push({
      subheader: 'Admin',
      items: [
        {
          title: 'Admin',
          path: BAKER_PATH_DASHBOARD.admin.root,
          icon: <AdminPanelSettingsIcon />,
          children: [
            { title: 'Baker Users', path: BAKER_PATH_DASHBOARD.admin.baker.users },
            { title: 'Client Portal Users', path: BAKER_PATH_DASHBOARD.admin.clientPortal.users },
            { title: 'Client Portfolio', path: BAKER_PATH_DASHBOARD.admin.clientPortal.portfolios },
            { title: 'Trusts Users', path: BAKER_PATH_DASHBOARD.admin.trusts.users },
            { title: 'Yield Book JSON payloads', path: BAKER_PATH_DASHBOARD.admin.yieldBookJsonPayloads }
          ]
        }
      ]
    });
  }

  // In Development — admin-only, work-in-progress tools that were previously in
  // the "Clients" section. Hidden from non-admins entirely (both menu + cards).
  // Route-level guards are intentionally left unchanged, so a non-admin with the
  // legacy portfolios/cashFlows flag can still reach these pages via deep link.
  if (isAdmin) {
    config.push({
      subheader: 'In Development',
      beta: true,
      // All leaf items (no nesting): the two bond searches carry "Search" in
      // their own labels rather than sitting under a "Search Bonds" sub-group.
      items: [
        {
          title: 'Cash Flows',
          path: BAKER_PATH_DASHBOARD.general.cashflows,
          icon: <CurrencyExchangeIcon />
        },
        {
          title: 'General Search',
          path: BAKER_PATH_DASHBOARD.search.general,
          icon: <SearchIcon />
        },
        {
          title: 'Muni Search',
          path: BAKER_PATH_DASHBOARD.search.muni,
          icon: <SearchIcon />
        }
      ]
    });
  }

  return config;
};
